import {
  createRehearsal,
  getCurrentRehearsal,
  getRehearsal,
  readRehearsal,
  recoverRehearsalEvents,
  sendRehearsalCommand,
  transitionRehearsal,
} from '@/services/localHub/localRehearsalService';

function initialState() {
  return {
    mode: 'CLOUD_MODE',
    session: null,
    bootstrap: null,
    events: [],
    lastEventSequence: 0,
    busy: false,
    error: null,
  };
}

function discovery(rootState) {
  const value = rootState.device.localControlDiscovery;
  if (!value?.url || !value?.profile?.hub_uuid) {
    throw new Error('HUB_UNREACHABLE');
  }
  return value;
}

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    setBusy(state, value) { state.busy = Boolean(value); },
    setError(state, value) { state.error = value || null; },
    setSession(state, value) {
      state.session = value || null;
      state.mode = value?.state === 'ACTIVE'
        ? 'LOCAL_REHEARSAL_MODE'
        : 'CLOUD_MODE';
    },
    setBootstrap(state, value) { state.bootstrap = value || null; },
    appendEvent(state, event) {
      if (
        !event
        || event.session_uuid !== state.session?.id
        || state.events.some((item) => item.event_uuid === event.event_uuid)
      ) return;
      state.events.push(event);
      state.events.sort((a, b) => a.event_sequence - b.event_sequence);
      state.lastEventSequence = Math.max(
        state.lastEventSequence,
        Number(event.event_sequence || 0),
      );
    },
    replaceEvents(state, payload) {
      state.events = payload?.events || [];
      state.lastEventSequence = Number(payload?.latest_sequence || 0);
    },
    reset(state) { Object.assign(state, initialState()); },
  },
  actions: {
    async discoverCurrent({ commit, rootState }) {
      const session = await getCurrentRehearsal(
        this, discovery(rootState),
      );
      commit('setSession', session);
      return session;
    },
    async create({ commit, rootState }) {
      commit('setBusy', true);
      commit('setError', null);
      try {
        const session = await createRehearsal(
          this, discovery(rootState),
        );
        commit('setSession', session);
        return session;
      } catch (error) {
        commit('setError', error?.response?.data?.detail || error.message);
        throw error;
      } finally {
        commit('setBusy', false);
      }
    },
    async transition({ commit, rootState, state }, transition) {
      if (!state.session?.id) throw new Error('REHEARSAL_SESSION_REQUIRED');
      commit('setBusy', true);
      commit('setError', null);
      try {
        const session = await transitionRehearsal(
          this, discovery(rootState), state.session.id, transition,
        );
        commit('setSession', session);
        return session;
      } finally {
        commit('setBusy', false);
      }
    },
    async refresh({ commit, rootState, state }) {
      if (!state.session?.id) return null;
      const session = await getRehearsal(
        this, discovery(rootState), state.session.id,
      );
      commit('setSession', session);
      return session;
    },
    async loadBootstrap({ commit, rootState, state }) {
      if (!state.session?.id) throw new Error('REHEARSAL_SESSION_REQUIRED');
      const response = await readRehearsal(
        this, discovery(rootState), state.session.id, 'pos/bootstrap',
      );
      commit('setBootstrap', response.data);
      return response.data;
    },
    async command({ commit, dispatch, rootState, state }, command) {
      if (state.mode !== 'LOCAL_REHEARSAL_MODE') {
        throw new Error('LOCAL_REHEARSAL_NOT_ACTIVE');
      }
      commit('setBusy', true);
      commit('setError', null);
      try {
        const response = await sendRehearsalCommand(
          this,
          discovery(rootState),
          {
            ...command,
            session_uuid: state.session.id,
            client_created_at: new Date().toISOString(),
          },
        );
        if (response.event) commit('appendEvent', response.event);
        await dispatch('loadBootstrap');
        return response;
      } catch (error) {
        commit('setError', error?.response?.data?.detail || error.message);
        throw error;
      } finally {
        commit('setBusy', false);
      }
    },
    async recoverEvents({ commit, rootState, state }) {
      if (!state.session?.id) return null;
      const response = await recoverRehearsalEvents(
        this, discovery(rootState), state.session.id,
        state.lastEventSequence,
      );
      response.events.forEach((event) => commit('appendEvent', event));
      return response;
    },
  },
};
